import { Box } from "@mui/material"
import { type FC, useState } from "react"

import { useGetCustomerAssociatedTestTemplates } from "../../hooks/useGetCustomerAssociatedTestTemplates"
import { useUpdateAssociationLocation } from "../../hooks/useUpdateAssociationLocation"

import { queryClient } from "@/providers/GraphqlRouterProvider"
import type { PreferenceAssociationItem } from "@/screens/CustomerProducts/hooks/useProductPreferenceAssociationList"
import { useProductPreferenceAssociationsStore } from "@/screens/CustomerProducts/stores/useProductPreferenceAssociationsStore"
import {
  AssociatedTestTemplatePostTestNoteSections,
  AssociatedTestTemplatePreTestNoteSection,
  AssociatedTestTemplatesTableSection,
} from "@/screens/Products/components/Product/Quality/TestTemplates/components/AssociatedTestTemplatesContainer"
import { ProductPostTestNoteFormDialog } from "@/screens/Products/components/Product/Quality/TestTemplates/components/ProductPostTestNoteFormDialog"
import { ProductPreTestNoteFormDialog } from "@/screens/Products/components/Product/Quality/TestTemplates/components/ProductPreTestNoteFormDialog"
import {
  useAssociatedTestTemplateColumns,
  useReorderAssociatedTestTemplate,
} from "@/screens/Products/components/Product/Quality/TestTemplates/hooks"
import type { ProductQualityInstructions } from "@/screens/Products/components/Product/Quality/TestTemplates/types"

enum CustomerQualityDialogType {
  PreTestNote = "PRE_TEST_NOTE",
  PostTestNote = "POST_TEST_NOTE",
}

type CustomerQualityAssociation = Pick<
  PreferenceAssociationItem,
  | "companyProductAssociationLocationId"
  | "shipToLocationId"
  | "preTestNotes"
  | "printExpirationToAkrochemCoa"
  | "printMfgDatesToAkrochemCoa"
  | "printPreTestNotesToAkrochemCoa"
  | "postTestNotes"
  | "printPostTestNotesToAkrochemCoa"
>

interface CustomerQualityPanelProps {
  association: CustomerQualityAssociation
  companyId: string
  productId: number
}

export const CustomerQualityPanel: FC<CustomerQualityPanelProps> = ({ association, companyId, productId }) => {
  const {
    preTestNotes,
    printExpirationToAkrochemCoa,
    printMfgDatesToAkrochemCoa,
    printPreTestNotesToAkrochemCoa,
    postTestNotes,
    printPostTestNotesToAkrochemCoa,
  } = association
  const companyProductAssociationLocationId = association.companyProductAssociationLocationId as string
  const locationId = association.shipToLocationId as string
  const [openedDialog, setOpenedDialog] = useState<CustomerQualityDialogType | null>(null)
  const [qualityInstructions, setQualityInstructions] = useState<ProductQualityInstructions>({
    preTestNotes: preTestNotes ?? null,
    printExpirationToAkrochemCoa: printExpirationToAkrochemCoa ?? false,
    printMfgDatesToAkrochemCoa: printMfgDatesToAkrochemCoa ?? false,
    printPreTestNotesToAkrochemCoa: printPreTestNotesToAkrochemCoa ?? false,
    postTestNotes: postTestNotes ?? null,
    printPostTestNotesToAkrochemCoa: printPostTestNotesToAkrochemCoa ?? false,
    isOperatorActionRequired: false,
    operatorActionDescription: null,
  })

  const executeSearch = useProductPreferenceAssociationsStore(({ executeSearch }) => executeSearch)

  const { associatedTestTemplates, isFetching, refetch } = useGetCustomerAssociatedTestTemplates({
    productId,
    companyId,
    locationId,
  })

  const columns = useAssociatedTestTemplateColumns(refetch)

  const { updateLocation, isUpdating } = useUpdateAssociationLocation()

  const { handleReorder } = useReorderAssociatedTestTemplate({ associatedTestTemplates, refetch })

  const handleDialogClose = () => {
    setOpenedDialog(null)
  }

  const handlePreTestNoteEdit = () => setOpenedDialog(CustomerQualityDialogType.PreTestNote)
  const handlePostTestNoteEdit = () => setOpenedDialog(CustomerQualityDialogType.PostTestNote)

  const handleNoteSave = async (input: ProductQualityInstructions) => {
    const merged = { ...qualityInstructions, ...input }
    const success = await updateLocation({
      id: companyProductAssociationLocationId,
      preTestNotes: merged.preTestNotes,
      printExpirationToAkrochemCoa: merged.printExpirationToAkrochemCoa ?? false,
      printMfgDatesToAkrochemCoa: merged.printMfgDatesToAkrochemCoa ?? false,
      printPreTestNotesToAkrochemCoa: merged.printPreTestNotesToAkrochemCoa ?? false,
      postTestNotes: merged.postTestNotes,
      printPostTestNotesToAkrochemCoa: merged.printPostTestNotesToAkrochemCoa ?? false,
    })
    if (success) {
      setQualityInstructions(merged)
      handleDialogClose()
      executeSearch(queryClient, { companyId, productId }, true)
    }
  }

  return (
    <>
      <Box className='flex flex-col gap-y-6'>
        <AssociatedTestTemplatePreTestNoteSection
          productQualityInstructions={qualityInstructions}
          onEdit={handlePreTestNoteEdit}
        />
        <AssociatedTestTemplatesTableSection
          productId={productId}
          associatedTestTemplates={associatedTestTemplates}
          columns={columns}
          isFetching={isFetching}
          onRefresh={refetch}
          onReorder={handleReorder}
          companyId={companyId}
          locationId={locationId}
        />
        <AssociatedTestTemplatePostTestNoteSections
          productQualityInstructions={qualityInstructions}
          onEdit={handlePostTestNoteEdit}
        />
      </Box>
      <ProductPreTestNoteFormDialog
        isOpen={openedDialog === CustomerQualityDialogType.PreTestNote}
        instructions={qualityInstructions}
        isUpdating={isUpdating}
        onClose={handleDialogClose}
        onSubmit={handleNoteSave}
      />
      <ProductPostTestNoteFormDialog
        isOpen={openedDialog === CustomerQualityDialogType.PostTestNote}
        instructions={qualityInstructions}
        isUpdating={isUpdating}
        onClose={handleDialogClose}
        onSubmit={handleNoteSave}
      />
    </>
  )
}
